import _ from "lodash";
import { paymentDispatcher } from "../../../actions/book/payment";
import { getExchangedPrice } from "../../../umbilical_hocs";
import { v4 } from "uuid";
import { createBooking } from "../../../api/activities";
import { sendPixelEvent } from "../../../helpers";

var website_api_key = import.meta.env.VITE_APP_WEBSITE_KEY;

export function initGuestData({ result }) {
  var leadParticipantFormStructure = {};

  function generateForm(data) {
    const form = {};
    Object.keys(data).forEach((key) => (form[key] = ""));
    return form;
  }

  var guests = [];
  if (result.customer_data_required && !result.participants_data_required) {
    guests.push({
      form: generateForm(result.customer_data),
      is_customer: true,
      guest_type: "ADT",
      guest_uuid: v4(),
    });
  }

  const adults = result.participants_data.filter(
    (part) => part.guest_type === "ADT"
  );
  const non_adults = result.participants_data.filter(
    (part) => part.guest_type !== "ADT"
  );

  if (result.participants_data_required) {
    adults.forEach((participant) =>
      new Array(participant.number_of_guests).fill(1).forEach((x, idx) => {
        const is_customer = idx === 0 && result.customer_data_required;
        var form = generateForm(participant.form);
        if (is_customer) {
          form = generateForm({
            ...participant.form,
            ...result.customer_data,
          });
        }
        var guest = {
          form,
          is_customer,
          item_uuid: participant.item_uuid,
          guest_type: participant.guest_type,
          guest_uuid: v4(),
          number_of_guests: participant.number_of_guests,
          product_id: participant.product_id,
        };
        guests.push(guest);
        if (is_customer) {
          leadParticipantFormStructure = participant;
        }
      })
    );

    non_adults.forEach((participant) =>
      new Array(participant.number_of_guests).fill(1).forEach((x) => {
        guests.push({
          form: generateForm(participant.form),
          is_customer: false,
          item_uuid: participant.item_uuid,
          guest_type: participant.guest_type,
          guest_uuid: v4(),
          number_of_guests: participant.number_of_guests,
          product_id: participant.product_id,
        });
      })
    );
  }
  return { guests, leadParticipantFormStructure };
}

export function initGuestFormStructure({ result, guests }) {
  const participantsArray = _.flatten(
    result.participants_data.map((participant) =>
      new Array(participant.number_of_guests).fill(1).map((x) => participant)
    )
  );

  const forms = guests.map((guest, idx) => {
    var form = {};
    if (guest.is_customer) {
      form = {
        ...form,
        ..._.get(result, "customer_data", {}),
        is_customer: true,
      };
    }

    if (result.participants_data_required) {
      form = {
        ...form,
        ...participantsArray[idx].form,
      };
    }

    return {
      ...form,
      guest_uuid: guest.guest_uuid,
    };
  });

  return forms;
}

// ---------------- Booking ------------------
export function initActivityPayment({
  booking_reference,
  amount,
  currency,
  page_session_id,
  activity_uid,
  customer_stripe_id,
  customer_uid,
  email,
  first_name,
  last_name,
  nationality,
  source_entity,
  dispatch,
}) {
  const srv_type = "ACT";
  const order_id = `${srv_type}___${booking_reference}`;

  const base_url = window.location.origin;
  const success_url = `${base_url}/activity/successful-booking/${page_session_id}`;
  const cancel_url = `${base_url}/activity_details/${page_session_id}?activity_uid=${activity_uid}`;
  const fail_url = `${base_url}/failed-booking/${page_session_id}`;

  sendPixelEvent("InitiateCheckout", {
    data: { product_type: "activity" },
    withPageView: false,
  });

  dispatch(
    paymentDispatcher({
      booking_reference,
      page_session_id,
      amount,
      currency,
      first_name,
      last_name,
      email,
      customer_uid,
      nationality,
      customer_stripe_id,
      order_id,
      success_url,
      cancel_url,
      fail_url,
      srv_type,
      source_entity,
    })
  );
}

export function createBookingPayload({
  prebookData,
  prebookUpdateData,
  session_id,
  guests,
  activity,
  website_name,
  customer_user_data,
  destination_meta,
  source_entity,
}) {
  const tmpGuests = [];
  guests.forEach((guest) =>
    tmpGuests.push({
      ...guest.form,
      guest_type: guest.guest_type,
      guest_uid: guest.guest_uuid,
      group_leader: guest.is_customer,
    })
  );

  if (!tmpGuests.some((g) => g.group_leader)) {
    const leadGuest = tmpGuests.find((g) => g.guest_type === "ADT");
    leadGuest.group_leader = true;
  }

  return {
    source_entity,
    website_meta: { api_key: website_api_key, name: website_name },
    provider: activity.provider,
    activity_uid: activity.uid,
    session_id,
    operation_datetime: prebookUpdateData.datetime,
    destination_meta: destination_meta,
    total_price: prebookUpdateData.full_price.value,
    currency: prebookUpdateData.full_price.currency,
    remarks: "",
    service_data: {
      details: activity,
      prebook: prebookData,
      prebook_update: prebookUpdateData,
    },
    guests: tmpGuests,
    customer_user_data,
  };
}

export async function createActivityBooking({
  prebookData,
  prebookUpdateData,
  session_id,
  guests,
  activity,
  website_name,
  customer_user_data,
  destination_meta,
  source_entity,
}) {
  const payload = createBookingPayload({
    prebookData,
    prebookUpdateData,
    session_id,
    guests,
    activity,
    website_name,
    customer_user_data,
    destination_meta,
    source_entity,
  });
  const result = await createBooking({ payload });

  return result;
}

export async function onCreateActivityBooking({
  prebookData,
  prebookUpdateData,
  session_id: search_session_id,
  guests,
  activity,
  customer_user_data,
  website_name,
  destination_meta: search_destination_meta,
  page_session_id,
  settedCurrency,
  customer_stripe_id,
  source_entity,
  dispatch,
  history,
}) {
  const bookingResult = await createActivityBooking({
    prebookData,
    prebookUpdateData,
    session_id: search_session_id,
    guests,
    activity,
    customer_user_data,
    website_name,
    destination_meta: search_destination_meta,
    source_entity,
  });

  if (
    !bookingResult ||
    !prebookUpdateData ||
    !page_session_id ||
    !guests ||
    !settedCurrency
  ) {
    history.push(`/failed-booking/${page_session_id}`);
    return null;
  }
  const price = _.get(bookingResult, "booking_result.payment_data.amount", 0);
  const priceCurrency = _.get(
    bookingResult,
    "booking_result.payment_data.currency",
    "USD"
  );
  const exchangedPrice = getExchangedPrice({
    price,
    priceCurrency,
    settedCurrency,
  });

  const booking_reference = _.get(bookingResult, "booking_result.reference");

  return {
    booking_reference,
    amount: exchangedPrice,
    currency: settedCurrency,
  };
}

export function getData({ prebook, search_session_id, history }) {
  var decoded = {};
  try {
    decoded = JSON.parse(window.atob(prebook));
  } catch (error) {
    history.push("/404/");
  }

  const output = {
    uid: decoded.uid,
    session_id: search_session_id,
    date: decoded.date,
    ticket_option: decoded.optn,
    items: decoded.items.map((item) => ({
      product_id: item.id,
      language: decoded.lng,
      quantity: item.qnt,
      pickup_code: item.pick ? item.pick : null,
      name: item.name,
      price: {
        currency: item.prc.cur,
        value: item.prc.val,
      },
    })),
  };
  return output;
}

export function createPrebookUpdatePayload({
  guests,
  activityUid,
  requiredData,
  leadPartFormStruct,
}) {
  var output = {
    uid: activityUid,
    customer_data_required: requiredData.customer_data_required,
    participants_data_required: requiredData.participants_data_required,
    customer_data: guests.find((guest) => guest.is_customer)?.form,
  };

  const product_guest_map = {};
  guests.map((guest) => (product_guest_map[guest.item_uuid] = []));
  guests.map((guest) => product_guest_map[guest.item_uuid].push(guest));

  if (requiredData.participants_data_required) {
    const participants_data = Object.entries(product_guest_map).map(
      ([item_uuid, guests]) => {
        const tmp = {
          item_uuid,
          number_of_guests: guests.length,
          forms: guests.map((g) => {
            if (g.is_customer) {
              var partFormKeys = [];
              if (!_.isEmpty(leadPartFormStruct)) {
                partFormKeys = Object.keys(leadPartFormStruct.form);
              }

              const custFormKeys = Object.keys(g.form);
              var validFormKeys = new Set(...custFormKeys);
              if (partFormKeys.length) {
                validFormKeys = new Set(
                  _.intersection(custFormKeys, partFormKeys)
                );
              }
              const form = {};
              Object.entries(g.form).forEach(([key, value]) =>
                validFormKeys.has(key) ? (form[key] = value) : null
              );
              return { ...form, guest_type: g.guest_type };
            }

            return { ...g.form, guest_type: g.guest_type };
          }),
        };
        return tmp;
      }
    );
    return { ...output, participants_data };
  }

  return output;
}

export const hiddenFields = [
  "musement_newsletter",
  "allow_profiling",
  "thirdparty_newsletter",
  "events_related_newsletter",
];

export function getGuestType({ guest_type, t }) {
  switch (guest_type) {
    case "ADT":
      return t("adult");
    case "CHD":
      return t("child");
    case "INF":
      return t("infant");

    default:
      return t("guest");
  }
}
